import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';

import { Callout, Panel, Skeleton, Toast } from '../../components';
import { ftuxSignal } from '../../features/ftux/signals';
import type { FtuxTargetId } from '../../features/ftux/targets';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { useToast } from '../../hooks/useToast';
import { ROUTE } from '../../routes';
import type { MailboxError } from '../../mock-data/mailbox-records';
import type { CreateMailboxBody, CreateMailboxResult } from './api/client';
import { mailboxService } from './api/service';
import { MAILBOX_SOURCE } from './api/source';
import {
  AddTiles,
  CreateMailboxView,
  MailboxTile,
  LoadFailedNotice,
  MailboxUnavailableNotice,
  PaymentSheet,
  RotatePasswordDialog,
  ProvisioningView,
} from './components';
import { Notice } from './components/DismissibleNotice/DismissibleNotice.styles';
import { MAILBOX_TILE_MIN_HEIGHT } from './components/MailboxTile/MailboxTile.styles';
import {
  createMailboxBody,
  mailboxDomainBlocksActions,
  mailboxShowsPending,
  mailboxDomainAwaitingSetup,
  mailboxPriceView,
  nextMailboxIsPaid,
  nextMailboxIsPaidByList,
  shownMailboxes,
} from './state';
import { PROVISION_ROWS } from './provisioning';
import { useProvisioningStep } from './useProvisioningStep';
import { stashFirstRun } from './firstRun';
import {
  freeSlotBusy,
  holdRefusal,
  holdSuccess,
  markCreateStarted,
  onHeldChange,
  settleCreate,
  takeRefusal,
  takeSuccess,
} from './heldRefusal';
import { useMailboxes } from './useMailboxes';
import { useOpenWebmail } from './webmail';
import { mailboxErrorText, takeMailboxError } from './errors';
import { usePurchase, type MailboxPaymentMethodId } from './usePurchase';
import { Page, Heading, Count, Meta, MetaDomain, Grid, BannerSlot } from './mailbox.styles';

/**
 * Mailbox, mounted at `/account/settings/mailbox`. The Settings
 * dispatcher (`pages/settings/section.tsx`) provides the chrome (`BackHeader` + the content cap),
 * so this renders only the section body: a panel heading, the add card, and one tile per
 * mailbox, in a grid (Figma OWS-Flow 46:877).
 *
 * Where the data comes from is the source switch (`config/mailboxSource.ts`): ows-mail when the
 * environment configures it, the fixtures only when a developer chose them, and otherwise the
 * section says the service is not available. A `useReducer` holds the mailboxes and entitlement,
 * persisted per brand on change. On the fixtures, each brand maps to one of the seed variants by list
 * position (cycling), so switching brands walks the section through every state with no demo control.
 *
 * It shares NO code with Scenario A (the `email` page folder) or Scenario B (the `mailboxes` page
 * folder) by design: the three brand-email scenarios coexist while the product direction is open,
 * and whichever scenario loses must delete whole with nothing else breaking. Any wiring here that
 * looks identical to Scenario B's is duplicated on purpose.
 *
 * Two views, swapped inside the section (not routes, so leaving unmounts them): the grid and the
 * create form. A paid create opens the checkout sheet OVER the form (the form stays mounted, so
 * the X returns to it with the draft intact); the invoice is minted only when Pay is pressed, so an
 * abandoned checkout leaves nothing behind. Creation is synchronous at the provider: a brand's first
 * create shows the provisioning screen from the click (or the settled payment) while the request runs
 * behind it; any later create spins the button. Once the create answers, the section moves to the new
 * mailbox's own page, first-run plaintext in hand (see `firstRun.ts`). Setup Mailbox on a tile opens
 * that same page. No tile offers Delete: it lives on the mailbox page's trash, behind the DELETE dialog.
 */
/** `provisioning`: a FIRST create was just submitted, and its paced setup checklist is on screen until
 *  the last row completes AND the create has answered (`answered`). A view state, not a route. */
type View = { kind: 'list' } | { kind: 'create' } | { kind: 'provisioning'; email: string; answered: boolean };
/** A submitted create: the address, the password, and whether the operator typed that password. */
type Draft = { email: string; password: string; typed: boolean };

/*
 * Where FTUX - Getting Started finds the section (inert outside an FTUX step): its panel and the grid of cards in it.
 * The shared Panel forwards no attribute, so a plain block round it carries its mark (the page lays it out as it laid
 * out the panel).
 */
const MAIL_PANEL_TARGET: FtuxTargetId = 'mail-panel';
const MAIL_SECTION_TARGET: FtuxTargetId = 'mail-section';

/**
 * The Mailbox section. With NO mail service in this environment (`MAILBOX_SOURCE === 'unavailable'`)
 * it says so and nothing else: no fixtures, no grid, no Create, no heading count, and no read at all
 * (the live section, and its `useMailboxes`, never mount). The Settings row stays, and opening it
 * explains. `MAILBOX_SOURCE` is fixed for the page's lifetime, so this choice never flips mid-render.
 */
export const MailboxSection = (): React.ReactElement =>
  MAILBOX_SOURCE === 'unavailable' ? (
    <Page data-testid="mailbox-section" data-source="unavailable">
      <MailboxUnavailableNotice />
    </Page>
  ) : (
    <MailboxSectionLive />
  );

const MailboxSectionLive = (): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The section is the one surface that polls the domain: it is where the setup notice shows.
  const {
    state,
    dispatch,
    domain,
    brandId,
    loading,
    usageOf,
    requestUsage,
    loadError,
    loadFailed,
    domainReadFailure,
    retryDomainRead,
    reloadList,
    price,
  } = useMailboxes({ pollDomain: true, readPrice: true });
  // support@ on screen only under its own "777supportmailbox" code (see `mailboxIsShown`).
  const showSupport = useFeatureGate().isEnabled('mailbox-support');
  // What a paid mailbox costs, from the service's answer and nothing else (see `mailboxPriceView`).
  const priceView = mailboxPriceView(price);
  // Charging switched off (`0`): a paid slot costs nothing, so its create skips the checkout.
  const chargingOff = priceView.kind === 'free';
  const { balance, mint, settle, cancel } = usePurchase(brandId, price.status === 'ready' ? price.price : null);
  const { toast, show, dismiss } = useToast();
  // Webmail: a fresh SSO call on every click, never before one.
  const onWebmail = useOpenWebmail(show, domain);
  const location = useLocation();

  // A delete on the mailbox page navigates here with a flag (no secret, just a word): say it once.
  const deletedElsewhere = (location.state as { deleted?: boolean } | null)?.deleted === true;
  React.useEffect(() => {
    if (!deletedElsewhere) return;
    show(t('Mailbox deleted.'));
    navigate(location.pathname, { replace: true, state: null });
  }, [deletedElsewhere, show, t, navigate, location.pathname]);

  // Setup Mailbox on a tile opens the mailbox's own page (its own route, so refresh and deep
  // links land there).
  const onServerSettings = React.useCallback((email: string) => navigate(ROUTE.mailboxDetail(email)), [navigate]);

  const [view, setView] = React.useState<View>({ kind: 'list' });
  // The mailbox a create has just made, waiting to be opened. Navigating is deferred to an effect
  // (below) rather than done in the create callback: leaving in the same tick unmounts this section
  // before `useMailboxes`' persist effect writes the new row, so the mailbox page (its own
  // `useMailboxes`) would not find it in storage and would bounce back as an unknown address.
  const [justCreated, setJustCreated] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState<string | null>(null);
  // The submitted draft a paid create is waiting to pay for. Held here, not in the reducer:
  // nothing exists until Pay settles.
  const draft = React.useRef<Draft | null>(null);
  // What a form brought BACK after a refused first create is seeded from: the provisioning screen had
  // replaced the form, so this is how the operator gets their address and password back.
  const [seed, setSeed] = React.useState<Draft | null>(null);
  // The server refused the last create (the address, and its error), and the 409's way forward: a
  // rotation of the existing mailbox's password, confirmed first, whose answer shows once in the form.
  const [refusal, setRefusal] = React.useState<{ email: string; error: MailboxError | null } | null>(null);
  const [rotateOpen, setRotateOpen] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);
  const [rotated, setRotated] = React.useState<{ password: string; setAt: string } | null>(null);

  // The purchase steps are Promises, which the hook's timers cannot cancel. A generation counter
  // does the same job: every flow captures the generation it started in and, after each await,
  // drops out if a brand switch or unmount has bumped it since, so a late create can never land on
  // a brand that is no longer shown.
  const flowGen = React.useRef(0);
  React.useEffect(
    () => () => {
      flowGen.current += 1;
    },
    [],
  );
  const stale = React.useCallback((gen: number): boolean => gen !== flowGen.current, []);

  // A brand switch invalidates the form's context (entitlement, existing names): back to the grid,
  // sheet closed, nothing in flight.
  React.useEffect(() => {
    flowGen.current += 1;
    setView({ kind: 'list' });
    setSubmitting(false);
    setSheetOpen(false);
    setPaying(false);
    setPayError(null);
    draft.current = null;
    setSeed(null);
    setRefusal(null);
    setRotateOpen(false);
    setRotating(false);
    setRotated(null);
    // A created mailbox waiting for its row belongs to the brand that made it: left pending, it would
    // keep the next brand's free card busy (and never find its row there).
    setJustCreated(null);
  }, [brandId]);

  // FTUX - Getting Started: the create view opening ends the mail piece, whichever handler opened it (Create free
  // mailbox runs onCreate). A no-op unless a piece listens.
  React.useEffect(() => {
    if (view.kind === 'create') ftuxSignal('mail-create-opened');
  }, [view.kind]);

  // A create that ANSWERED after the operator left (the setup screen invited them to; see
  // `heldRefusal.ts`). A refusal: the form returns exactly as it was left, with the refusal where it
  // renders. A success: the list is re-read from the service (the mock, which has none, takes the
  // create a normal one dispatches) and the mailbox's page opens once the row is there, where its
  // first-run password shows once. Taken on mount (it landed while they were away) and whenever one
  // is held while this section is on screen (they came back before the create answered). Only onto
  // the grid, and only once the list has been read and actions are allowed: an operator already in
  // another flow keeps it held rather than having their form replaced, and a form is never priced
  // against an unread list. Runs after the reset above, in the same commit, so the reset cannot wipe
  // it.
  const viewKind = React.useRef(view.kind);
  React.useEffect(() => {
    viewKind.current = view.kind;
  }, [view.kind]);
  const heldCanShow = !loading && !loadFailed && !mailboxDomainBlocksActions(state.mailDomain);
  React.useEffect(() => {
    if (!heldCanShow) return undefined;
    const deliver = (): void => {
      if (viewKind.current !== 'list') return;
      const back = takeRefusal(brandId, domain);
      if (back) {
        setSeed(back.draft);
        setRefusal({ email: back.draft.email, error: back.error });
        setView({ kind: 'create' });
        return;
      }
      const done = takeSuccess(brandId, domain);
      if (!done) return;
      if (MAILBOX_SOURCE === 'http') reloadList();
      else
        dispatch({
          type: 'create',
          email: done.email,
          domain,
          createdAt: new Date().toISOString(),
          billing: done.billing,
          invoiceId: done.invoiceId,
          mailDomain: done.mailDomain,
          withSystemMailbox: done.steps.length > 1,
        });
      // Opens the mailbox's page once its row is in the list (the effect on `justCreated` below).
      setJustCreated(done.email);
    };
    deliver();
    return onHeldChange(brandId, domain, deliver);
    // `view.kind` re-runs it on the way back to the grid, so an answer held while the operator was in
    // another flow (the form, say) is shown when they return to the list in this same visit.
  }, [brandId, domain, heldCanShow, view.kind, reloadList, dispatch]);

  // THE DOUBLE-CHARGE GUARD: while a free-slot create is in flight (here or from a section the
  // operator has since left) or its success is held and not yet taken, the free add card is disabled
  // with its own label. Offering it again would make the second create `paid`. The module is not
  // React state, so a change there bumps `heldTick` to re-render, and the guard is read fresh each time.
  const [, setHeldTick] = React.useState(0);
  React.useEffect(() => onHeldChange(brandId, domain, () => setHeldTick((n) => n + 1)), [brandId, domain]);
  // Busy until the created mailbox's ROW is in the list, not merely until its held answer is taken:
  // `justCreated` clears only once the row arrives (the effect that opens its page), so a re-read that
  // lags the create, or the render between taking the answer and the re-read starting, never offers
  // the free create again.
  const freeBusy = freeSlotBusy(brandId, domain) || justCreated !== null;

  // The slot the NEXT create takes, one rule for the add card, the form and whether the checkout
  // opens: on the live path it reads the service's list (free while no live `free` mailbox exists);
  // the mock keeps its local entitlement.
  const nextPaid =
    MAILBOX_SOURCE === 'http' ? nextMailboxIsPaidByList(state.mailboxes) : nextMailboxIsPaid(state.entitlement);
  const onCreate = React.useCallback(() => {
    setRefusal(null);
    setRotated(null);
    setSeed(null);
    setView({ kind: 'create' });
  }, []);
  // The free card's action, gated in the handler too (not only by the disabled button): never a second
  // free create while the first is in flight or its success is held.
  const onCreateFree = React.useCallback(() => {
    if (freeSlotBusy(brandId, domain) || justCreated !== null) return;
    onCreate();
  }, [brandId, domain, justCreated, onCreate]);
  // A refused (or unanswered) first create: the provisioning screen goes, and the form comes back as
  // the operator left it (address, password, whether it was typed), the refusal where it renders.
  const returnToForm = React.useCallback((held: Draft) => {
    setSeed(held);
    setView({ kind: 'create' });
  }, []);
  const onCancelCreate = React.useCallback(() => {
    if (submitting || sheetOpen || rotating) return;
    setRefusal(null);
    setRotated(null);
    setView({ kind: 'list' });
  }, [submitting, sheetOpen, rotating]);
  // An edit makes a refusal stale, except a 409: it is about one ADDRESS, and the form's
  // address-keyed gate decides it (typing another address steps it aside, typing the refused one
  // back brings it and its offer back), so a keystroke can never reopen a refused address for a
  // retry. Nor once a rotation has answered: that password is shown ONCE, and a keystroke must
  // not take it off the screen.
  const onEdited = React.useCallback(() => {
    if (!rotated && refusal?.error?.code !== 'conflict') setRefusal(null);
  }, [rotated, refusal]);

  // The 402's way forward: add funds to the main wallet, on the app's own wallet page (where every
  // other top-up of the main wallet happens). The form keeps nothing: the create can be made again.
  const onTopUp = React.useCallback(() => navigate(ROUTE.wallet()), [navigate]);
  // The Newton showcase shows no owner wallet, so the 402 offers no top-up there (the wallet page goes Home).
  const newtonShowcase = useAppSelector(selectNShowcase);

  // The 409's way forward: confirm (every mail client is signed out) and choose the password, then
  // rotate to it.
  const onRotate = React.useCallback(() => {
    if (refusal?.error?.code === 'conflict' && !rotating) setRotateOpen(true);
  }, [refusal, rotating]);
  const onRotateCancel = React.useCallback(() => setRotateOpen(false), []);
  const onRotateConfirm = React.useCallback(
    (chosen: string) => {
      setRotateOpen(false);
      const target = refusal?.email;
      if (!target || rotating) return;
      const gen = flowGen.current;
      setRotating(true);
      void mailboxService.rotatePassword(domain, target, chosen).then(
        ({ password }) => {
          // The old password is already dead: if the section has moved on, the new one is held for
          // the mailbox page rather than dropped (it exists nowhere else).
          if (stale(gen)) {
            stashFirstRun(target, password);
            return;
          }
          setRotating(false);
          setRotated({ password, setAt: new Date().toISOString() });
        },
        // A refused rotation leaves the 409 standing (its offer, and the gate on this address): the
        // operator chose this password in the dialog, so a provider `400` on it is an ordinary answer,
        // and it is about the rotation, not the create. Said as a toast, as on the mailbox page.
        (rejection: unknown) => {
          if (stale(gen)) return;
          setRotating(false);
          show(mailboxErrorText(takeMailboxError(rejection), t, t('The password could not be changed. Try again in a moment.')));
        },
      );
    },
    [refusal, rotating, stale, domain, show, t],
  );

  /**
   * Bring a mailbox into being: the row (born `active` with its password set: the create already
   * returned), the plaintext stashed for the mailbox page's first run, then the mailbox's own page.
   * Shared by the free path and the paid path once its invoice has settled.
   */
  const materialise = React.useCallback(
    (email: string, created: CreateMailboxResult, onScreen: boolean, billing: 'free' | 'paid', invoiceId?: string) => {
      const createdAt = new Date().toISOString();
      const { credentials, mailDomain } = created;
      // On the mock, a create that set the domain up (more than `mailbox_created`) also made support@,
      // as the service does; live, the list re-read on the next mount already carries it.
      const withSystemMailbox = MAILBOX_SOURCE !== 'http' && created.steps.length > 1;
      dispatch({ type: 'create', email, domain, createdAt, billing, invoiceId, mailDomain, withSystemMailbox });
      stashFirstRun(email, credentials.password);
      // On the provisioning screen (a first create, decided before the request), the answer lets its
      // last row complete, and the screen moves to the mailbox's page on its own. The answer's `steps`
      // stays the authority: a create that looked like a first one but set nothing up (only
      // `mailbox_created`) skips the remaining rows. A later mailbox's create: straight to its page.
      if (onScreen && created.steps.length > 1) setView({ kind: 'provisioning', email, answered: true });
      else setJustCreated(email);
    },
    [dispatch, domain],
  );

  // Setup can fail on this screen only AFTER the mailbox exists (the create has answered): the
  // domain's setup failed at the provider, or reading its status did. A failed create never gets here;
  // it returns to the form. While a failure shows, the checklist holds where it is (the pacing never
  // walks past a failure) and Try again is the operator's.
  const provisioningFailure: { reason: string } | null =
    view.kind !== 'provisioning' || !view.answered
      ? null
      : state.mailDomain?.status === 'failed'
        ? { reason: state.mailDomain.failureReason ?? t('The provider did not say why. Contact support.') }
        : domainReadFailure
          ? {
              reason: mailboxErrorText(
                domainReadFailure.error,
                t,
                t('The setup status could not be checked. Try again in a moment.'),
              ),
            }
          : null;
  // The provisioning screen's step: every row paced, one at a time, whatever DNS has done (the
  // section's setting-up notice tells that truth); the last one also waits for the create's answer.
  // Held while a failure shows. Called unconditionally (hooks).
  const provisioningEmail = view.kind === 'provisioning' ? view.email : null;
  const provisioningStep = useProvisioningStep(
    provisioningEmail,
    provisioningFailure !== null,
    view.kind === 'provisioning' && view.answered,
  );
  const provisioningDone = provisioningStep >= PROVISION_ROWS;
  // The last row completed (so the create has answered): move to the mailbox's page (through the deferred navigation below, which
  // waits for the row to be in state). No button and no hold: the sequence is the wait.
  // Once per mailbox: the navigation below clears `justCreated` before the section unmounts.
  const advancedFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (view.kind !== 'provisioning' || !provisioningDone || advancedFor.current === view.email) return;
    advancedFor.current = view.email;
    setJustCreated(view.email);
  }, [view, provisioningDone]);

  // Open the new mailbox's page once its row is in state. This effect is declared after
  // `useMailboxes()`, so in the commit that adds the row the hook's persist effect has already
  // written it to storage by the time this runs.
  React.useEffect(() => {
    if (justCreated === null || !state.mailboxes.some((m) => m.email === justCreated)) return;
    setJustCreated(null);
    navigate(ROUTE.mailboxDetail(justCreated));
  }, [justCreated, state.mailboxes, navigate]);

  // The domain's first mailbox (no live one yet). Decides both what the create carries and, BEFORE the
  // request, whether the provisioning screen shows: the two must never disagree.
  const firstCreate = !state.mailboxes.some((m) => m.status !== 'deleted');

  /**
   * The create request (see `createMailboxBody`): the address, the size and the password on every
   * create; the domain's FIRST mailbox (no live one yet) also carries `manageDns`, which registers
   * the domain. Nothing about the casino, tenant, site or owner: the service resolves those from the
   * owner's token.
   */
  const createBody = React.useCallback(
    (email: string, password: string): CreateMailboxBody => createMailboxBody(email, password, firstCreate),
    [firstCreate],
  );

  /**
   * Create. The name and the password are already validated by the form (the single validation
   * site), so a rejected name never reaches here and no invoice is ever minted for one.
   *  - included (free), or paid with charging switched off: a first create shows the provisioning
   *    screen at once, with the request behind it; a later one spins the button. Either way the row
   *    appears, already `active`, once the create answers, and the section moves to its page. Never
   *    touches the purchase seam.
   *  - paid: NOTHING is dispatched. The draft is held and the checkout sheet opens over the form.
   *    The invoice is minted only on Pay, so closing the sheet leaves no half-made mailbox and no
   *    pending invoice: the flow is pay, then create.
   */
  const onSubmit = React.useCallback(
    (email: string, password: string, typed: boolean) => {
      if (submitting || sheetOpen || rotating) return;
      // A new submit is a new address's answer: the last refusal AND its rotation go, so a rotated
      // password can never be shown under another address's 409.
      setRefusal(null);
      setRotated(null);
      const held: Draft = { email, password, typed };
      if (!nextPaid || chargingOff) {
        const slot: 'free' | 'paid' = nextPaid ? 'paid' : 'free';
        const gen = flowGen.current;
        const onScreen = firstCreate;
        setSubmitting(true);
        // The switch happens here, on the click, not in the answer: the request runs behind the screen.
        if (onScreen) setView({ kind: 'provisioning', email, answered: false });
        // The free slot: in flight until it answers, so no other free create is offered meanwhile.
        const attempt = markCreateStarted(brandId, domain, { freeSlot: slot === 'free' });
        void mailboxService.createMailbox(domain, createBody(email, password)).then(
          (created) => {
            // The mailbox exists now, and this answer is the only copy of its password: hold it for
            // the mailbox page BEFORE the stale check, so leaving mid-create does not lose it.
            stashFirstRun(email, created.credentials.password);
            settleCreate(brandId, domain, attempt);
            if (stale(gen)) {
              // The operator left: hold the success for their return, or the grid would offer the
              // free create again and the second one would be charged.
              holdSuccess(brandId, domain, attempt, {
                email,
                billing: slot,
                mailDomain: created.mailDomain,
                steps: created.steps,
              });
              return;
            }
            setSubmitting(false);
            // The password shown is the one the create returned, once.
            materialise(email, created, onScreen, slot);
          },
          (rejection: unknown) => {
            settleCreate(brandId, domain, attempt);
            const error = takeMailboxError(rejection);
            if (stale(gen)) {
              // The operator left the provisioning screen, as it said they could: hold the refusal for
              // their return rather than lose it.
              if (onScreen) holdRefusal(brandId, domain, attempt, { draft: held, error });
              return;
            }
            setSubmitting(false);
            if (onScreen) returnToForm(held);
            setRefusal({ email, error });
          },
        );
        return;
      }
      draft.current = held;
      setPayError(null);
      setSheetOpen(true);
    },
    [submitting, sheetOpen, rotating, nextPaid, chargingOff, firstCreate, stale, materialise, domain, brandId, createBody, returnToForm],
  );

  // The X: nothing was minted, so nothing to cancel; the form is still mounted with its draft.
  const onCloseSheet = React.useCallback(() => {
    if (paying) return;
    setSheetOpen(false);
    setPayError(null);
  }, [paying]);

  /**
   * Pay: mint the invoice, settle it by the chosen method, THEN create the mailbox. A wallet
   * settlement moves the (stand-in) balance through the purchase hook; a card settlement leaves it
   * alone. A settlement that fails cancels the invoice and leaves the sheet OPEN with an error line
   * in it (the operator is looking at the sheet), so they can try again or close it; nothing is
   * dispatched, so nothing is half-made.
   */
  const onPay = React.useCallback(
    (method: MailboxPaymentMethodId) => {
      const held = draft.current;
      if (!held || paying) return;
      const gen = flowGen.current;
      setPaying(true);
      setPayError(null);
      void (async () => {
        let invoiceId: string | null = null;
        try {
          invoiceId = (await mint()).invoiceId;
          if (stale(gen)) return;
          await settle(invoiceId, method);
          if (stale(gen)) return;
        } catch {
          if (stale(gen)) return;
          if (invoiceId) void cancel(invoiceId);
          setPaying(false);
          setPayError(t('Payment did not go through. Nothing was charged. Try again, or close this and come back.'));
          return;
        }
        // Paid, now create. A first create's provisioning screen appears now, once the sheet has
        // settled (not when it opened), with the request behind it.
        const onScreen = firstCreate;
        if (onScreen) {
          setSheetOpen(false);
          setView({ kind: 'provisioning', email: held.email, answered: false });
        }
        // The flow is pay, then create, so a refusal here arrives AFTER the
        // settlement: the invoice is cancelled through the same seam a failed settlement uses, the
        // sheet closes, and the form shows the service's words. (The DEV wallet ledger models no
        // refund, so a wallet-paid demo refusal leaves its stand-in debit; the real cancel is the
        // billing system's to honour.)
        let created: Awaited<ReturnType<typeof mailboxService.createMailbox>>;
        const attempt = markCreateStarted(brandId, domain);
        try {
          created = await mailboxService.createMailbox(domain, createBody(held.email, held.password));
        } catch (rejection) {
          // The settled invoice is cancelled whatever the section is doing now: leaving must not keep
          // a charge for a mailbox that was never made.
          void cancel(invoiceId);
          const error = takeMailboxError(rejection);
          if (stale(gen)) {
            if (onScreen) holdRefusal(brandId, domain, attempt, { draft: held, error });
            return;
          }
          setPaying(false);
          setSheetOpen(false);
          draft.current = null;
          if (onScreen) returnToForm(held);
          setRefusal({ email: held.email, error });
          return;
        }
        // As on the free path: the password is held before the stale check, so it survives leaving,
        // and so is the success itself.
        stashFirstRun(held.email, created.credentials.password);
        if (stale(gen)) {
          holdSuccess(brandId, domain, attempt, {
            email: held.email,
            billing: 'paid',
            invoiceId,
            mailDomain: created.mailDomain,
            steps: created.steps,
          });
          return;
        }
        setPaying(false);
        setSheetOpen(false);
        draft.current = null;
        materialise(held.email, created, onScreen, 'paid', invoiceId);
      })();
    },
    [paying, mint, settle, cancel, stale, materialise, t, domain, brandId, createBody, firstCreate, returnToForm],
  );

  // The domain's status, re-derived on every render from the reducer (a finished poll, the walk
  // back after the last delete), never cached here.
  const { mailDomain } = state;
  const domainStatus = mailDomain?.status;
  // Every mailbox waits to be set up while the domain is still being provisioned (see the rule).
  const awaitingDomain = mailboxDomainAwaitingSetup(mailDomain, state.mailboxes);
  // Live source only: the brand's mailboxes could not be read. The section is then empty, and a read
  // that failed must not pass for a brand with no mailboxes: it says so (below) and, like a failed
  // domain, offers nothing to press. A create here would be priced against a list nobody has seen.
  const readFailed = loadFailed;
  const blocked = mailboxDomainBlocksActions(mailDomain) || readFailed;
  // `404` and `[]` are different empty states: a domain the service has never heard of has no mail
  // service at all (the first create sets it up), while a registered one simply has no mailboxes.
  const domainUnknown = mailDomain === null && !readFailed;

  // What the operator SEES: the grid and the heading count read this, and nothing else does. Every
  // rule (the setting-up notice, the poll in `useMailboxes`, the free slot, the first create) reads
  // the full list, support@ included, whether or not it is shown (see `mailboxIsShown`). The count says what
  // is on screen: support@ counts only while it is shown ("777supportmailbox"), Pending or not.
  const shown = shownMailboxes(state.mailboxes, showSupport);
  const count = shown.length;
  const freeAvailable = !nextPaid;

  if (view.kind === 'provisioning') {
    return (
      <Page>
        <ProvisioningView
          address={view.email}
          step={provisioningStep}
          failed={provisioningFailure !== null && !provisioningDone}
          failureReason={provisioningFailure?.reason}
          // "Try again" RE-READS the domain, and that is all it may do. It must NEVER re-send the
          // create: the failure branch only shows once the create has answered, so the mailbox
          // already exists, and a second create is a 409 at best and something worse at worst.
          onRetry={retryDomainRead}
        />
        <Toast toast={toast} onDismiss={dismiss} />
      </Page>
    );
  }

  if (view.kind === 'create') {
    return (
      <Page>
        <CreateMailboxView
          domain={domain}
          mailboxes={state.mailboxes}
          paid={nextPaid}
          price={priceView}
          submitting={submitting || sheetOpen || rotating}
          onSubmit={onSubmit}
          onClose={onCancelCreate}
          refusal={refusal}
          onEdited={onEdited}
          onRotate={onRotate}
          onTopUp={newtonShowcase ? undefined : onTopUp}
          rotating={rotating}
          rotated={rotated}
          initialLocalPart={seed ? seed.email.slice(0, seed.email.lastIndexOf('@')) : undefined}
          initialPassword={seed?.password}
          initialPasswordTyped={seed?.typed}
        />
        <RotatePasswordDialog
          open={rotateOpen}
          address={refusal?.email ?? ''}
          onCancel={onRotateCancel}
          onConfirm={onRotateConfirm}
        />
        <Toast toast={toast} onDismiss={dismiss} />
        {/* Over the form, not instead of it: the draft survives the X. Wallet balance arrives
            matched-only from the purchase hook, so an unmatched wallet reads as unknown and the
            card is offered. */}
        <PaymentSheet
          open={sheetOpen}
          price={priceView}
          walletBalance={balance}
          paying={paying}
          error={payError}
          onPay={onPay}
          onClose={onCloseSheet}
        />
      </Page>
    );
  }

  return (
    <Page
      data-testid="mailbox-section"
      data-domain-status={loading || readFailed ? undefined : (domainStatus ?? 'not_found')}
    >
      {/* THE DOMAIN'S FAILURE NOTICES: one at the top of the section, never one per tile, because the
          domain is the section's. No dismiss on either: each reflects live state and disappears on
          its own when the state does. (The setting-up notice sits inside the panel, under the
          heading: see there.) */}
      {!loading && readFailed ? (
        <div data-testid="mailbox-read-failed-notice">
          {/* A session that ended (401), a domain that is not this owner's (403, the service's own
              words), or the service's words / ours for anything else. */}
          <LoadFailedNotice error={loadError} onRetry={reloadList} />
        </div>
      ) : null}
      {!loading && domainStatus === 'failed' ? (
        <div data-testid="mailbox-domain-failed-notice">
          {/* The provider's own reason, verbatim: it is written for an operator, and a generic line
              in its place would hide the one thing that says what to fix. */}
          {mailDomain?.failureReason ? (
            <Callout tone="danger" title="Mail setup failed" text={mailDomain.failureReason} />
          ) : (
            <Callout tone="danger" title="Mail setup failed" text="The provider did not say why. Contact support." />
          )}
        </div>
      ) : null}
      <div data-ftux={MAIL_PANEL_TARGET}>
        <Panel>
          <Heading>
            {/* Singular and plural are two keys chosen by count, not a concatenation: the catalog
                carries no plural suffix variants, and the plural key is the one the area already
                translates. */}
            {/* The heading states only what has been READ: nothing while the mailboxes load (the live
                source answers after a real round trip, and until then the state is the empty start or
                the previous brand's rows), and nothing after a failed read (the notice says why). */}
            {loading || readFailed ? null : count === 0 && domainUnknown ? (
              <Count text="No mail on {{domain}} yet" placeholders={{ domain }} tag="span" />
            ) : count === 0 ? (
              <Count text="No mailboxes yet" tag="span" />
            ) : count === 1 ? (
              <Count text="1 mailbox" tag="span" />
            ) : (
              <Count text="{{n}} mailboxes" placeholders={{ n: count }} tag="span" />
            )}
            {loading || readFailed ? null : count > 0 ? (
              <Meta data-testid="mailbox-heading-meta">
                {/* The domain alone: every card carries its own slot pill, so counts here would restate
                    what is on screen below and compete with the title. */}
                <MetaDomain>{domain}</MetaDomain>
              </Meta>
            ) : domainUnknown ? (
              <Meta data-testid="mailbox-heading-meta">
                <span>{t('Your first mailbox sets up mail for this domain.')}</span>
              </Meta>
            ) : null}
          </Heading>
          {/* THE SETTING-UP NOTICE, in the section's one banner position: inside the panel, directly
              under the heading, in the create form's price-banner container (`Notice`, which gives the
              info tone a visible border instead of the panel hairline), WITHOUT its X. It shows for as
              long as the domain is being set up, from T0 until its mail works, by the SAME rule the
              cards' waiting line reads (`awaitingDomain`, see `mailboxDomainAwaitingSetup`), so the
              notice and the cards under it can never say different things. No dismiss, deliberately:
              it reflects live state and disappears on its own when the domain is ready (the poll, on
              the same rule, moves it on), and it is the only thing on screen saying why incoming mail
              has not arrived. A banner an operator can close is a banner they will close. */}
          {!loading && awaitingDomain ? (
            <BannerSlot data-testid="mailbox-dns-pending-notice">
              <Notice
                tone="info"
                title="Incoming mail is on its way"
                text="Your mailboxes can be set up once the domain is ready to receive mail. That usually takes a few minutes, and can take up to 48 hours to reach every network."
              />
            </BannerSlot>
          ) : null}

          {/* No mailbox card: the add tile alone takes the whole row rather than half of it. */}
          <Grid data-testid="mailbox-grid" data-ftux={MAIL_SECTION_TARGET} $single={!loading && shown.length === 0}>
            {loading ? (
              <>
                {/* The same height as the tiles that replace them, so the grid does not jump. */}
                <Skeleton block height={MAILBOX_TILE_MIN_HEIGHT} radius={14} />
                <Skeleton block height={MAILBOX_TILE_MIN_HEIGHT} radius={14} />
                <Skeleton block height={MAILBOX_TILE_MIN_HEIGHT} radius={14} />
                <Skeleton block height={MAILBOX_TILE_MIN_HEIGHT} radius={14} />
              </>
            ) : (
              <>
                {/* A failed domain takes every mailbox action away, creating one included. */}
                {blocked ? null : (
                  <AddTiles
                    domain={domain}
                    freeAvailable={freeAvailable}
                    price={priceView}
                    freeBusy={freeBusy}
                    onCreateFree={onCreateFree}
                    onCreatePaid={onCreate}
                  />
                )}
                {shown.map((mailbox) => (
                  <MailboxTile
                    key={mailbox.email}
                    mailbox={mailbox}
                    usage={usageOf(mailbox)}
                    onVisible={requestUsage}
                    onWebmail={onWebmail}
                    onServerSettings={onServerSettings}
                    inert={blocked}
                    // support@ while its domain is being set up, when it is shown: a Pending pill, the rest of the card its own.
                    pending={mailboxShowsPending(mailDomain, mailbox)}
                    awaitingDomain={awaitingDomain}
                  />
                ))}
              </>
            )}
          </Grid>
        </Panel>
      </div>

      <Toast toast={toast} onDismiss={dismiss} />
    </Page>
  );
};

export default MailboxSection;
